import { _getBaseStoreInstance } from '../stores/_baseStore';

/**
 * A single Server-Sent-Event parsed off the wire.
 */
export interface SSEEvent {
  /** The SSE `event:` name. Defaults to 'message' when the server omits it. */
  event: string;
  /** The joined `data:` payload (raw, NOT trimmed — callers parse/trim). */
  data: string;
}

export interface StreamSSEOptions {
  /** Absolute URL to POST to (caller builds it from its own host + path). */
  url: string;
  /** JSON-serialisable request body. */
  body: unknown;
  /** Aborts the in-flight request/stream when triggered. */
  signal?: AbortSignal;
  /**
   * Bearer token. If omitted it's resolved from the global authentication
   * store. On a 401/406 the token is refreshed and the request retried once.
   */
  jwtToken?: string;
  /** Extra headers merged on top of the defaults. */
  headers?: Record<string, string>;
  /** Called once per fully-received SSE event. */
  onEvent: (event: SSEEvent) => void;
  /** Called when the stream finishes normally (or on a `[DONE]` sentinel). */
  onComplete?: () => void;
  /** Called on a non-abort error. AbortError is swallowed silently. */
  onError?: (error: Error) => void;
}

/**
 * Generic Server-Sent-Events POST stream reader.
 *
 * Why this exists: several features (the chat bot, the Track Record AI
 * extraction agent) need to consume a streaming HTTP response, but the
 * axios-based `Service` base class can't stream. Rather than each service
 * hand-rolling the same fetch + auth (+401/406 refresh-retry) + ReadableStream
 * decode + SSE framing, that plumbing lives here once. Callers only parse their
 * own event types inside `onEvent` (e.g. 'trace' / 'result' / 'token').
 */
export async function streamSSE(options: StreamSSEOptions): Promise<void> {
  const { url, body, signal, headers: extraHeaders, onEvent, onComplete, onError } = options;

  const resolveToken = async (forceRefresh: boolean): Promise<string | null> => {
    if (options.jwtToken && !forceRefresh) return options.jwtToken;
    const store = _getBaseStoreInstance();
    return (await store?.authenticationStore?.getJwtToken?.(forceRefresh)) ?? null;
  };

  const buildHeaders = (token: string | null): Record<string, string> => ({
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  });

  const doFetch = (token: string | null) =>
    fetch(url, {
      method: 'POST',
      headers: buildHeaders(token),
      body: JSON.stringify(body),
      signal,
      credentials: 'include',
    });

  try {
    let token = await resolveToken(false);
    let response = await doFetch(token);

    // Token may have expired. Spring returns 406 here because it can't render a
    // 401 error as text/event-stream. Refresh once and retry, mirroring the
    // axios interceptor in service.ts.
    if (response.status === 401 || response.status === 406) {
      token = await resolveToken(true);
      if (token) response = await doFetch(token);
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is null');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = 'message';
    let currentDataLines: string[] = [];

    const flush = () => {
      const event = currentEvent || 'message';
      const data = currentDataLines.join('\n');
      currentEvent = 'message';
      currentDataLines = [];
      if (!data) return;
      onEvent({ event, data });
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const rawLine of lines) {
        const line = rawLine.replace(/\r$/, '');

        if (line === '') {
          flush();
          continue;
        }
        if (line.startsWith('event:')) {
          if (currentDataLines.length > 0) flush();
          currentEvent = line.substring(6).trim();
          continue;
        }
        if (line.startsWith('data:')) {
          // Don't trim: leading spaces can be significant in the payload.
          const dataPart = line.substring(5);
          if (dataPart.trim() === '[DONE]') {
            onComplete?.();
            return;
          }
          currentDataLines.push(dataPart);
          continue;
        }
      }
    }

    // Flush any event left without a trailing blank line, then signal end.
    flush();
    onComplete?.();
  } catch (error) {
    // A cancelled stream is expected (caller aborted) — not an error.
    if ((error as Error).name === 'AbortError') {
      return;
    }
    onError?.(error as Error);
  }
}
